/* Add one explicit, one-time item; Shopify keeps existing lines, plans and codes.
 * A failed render must never cause the successful add request to be retried. */
if (!customElements.get('ampm-cart-addons')) {
  customElements.define('ampm-cart-addons', class extends HTMLElement {
    static busy = false;

    connectedCallback() {
      this.submit = this.onSubmit.bind(this);
      this.addEventListener('submit', this.submit);
      const drawer = this.closest('cart-drawer');
      if (drawer && !drawer.hasAttribute('open')) {
        this.querySelectorAll('a, button').forEach(control => { control.tabIndex = -1; });
      }
    }

    disconnectedCallback() {
      this.removeEventListener('submit', this.submit);
    }

    sectionsToRender() {
      const pageItems = document.querySelector('cart-items');
      if (!this.closest('cart-drawer') && pageItems?.getSectionsToRender) {
        return pageItems.getSectionsToRender();
      }
      return [
        { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
      ];
    }

    async onSubmit(event) {
      const form = event.target.closest('[data-addon-form]');
      if (!form || !this.contains(form)) return;
      event.preventDefault();
      if (this.constructor.busy || form.querySelector('button').disabled) return;
      this.constructor.busy = true;
      const buttons = [...this.querySelectorAll('button')];
      const button = form.querySelector('button');
      const originalLabel = button.textContent;
      const message = this.querySelector('[data-addon-message]');
      const review = this.querySelector('[data-addon-review]');
      const cartUrl = this.dataset.cartUrl;
      const inDrawer = !!this.closest('cart-drawer');
      const surface = this.closest('cart-drawer') || document.getElementById('main-cart-footer');
      const checkoutControls = [...(surface?.querySelectorAll('[name="checkout"], .wt-cart__cta') || [])]
        .map(control => ({ control, disabled: control.disabled }));
      let added = false;
      let definitiveRejection = false;
      buttons.forEach(control => { control.disabled = true; });
      checkoutControls.forEach(({ control }) => { control.disabled = true; });
      button.textContent = 'Adding…';
      button.setAttribute('aria-busy', 'true');
      message.hidden = true;
      review.hidden = true;

      try {
        const sections = this.sectionsToRender();
        const response = await fetch(this.dataset.addUrl, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            items: [{ id: form.elements.id.value, quantity: 1 }],
            sections: sections.map(section => section.section),
            sections_url: window.location.pathname
          })
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          definitiveRejection = response.status === 422;
          throw new Error(definitiveRejection ? data.description || 'This item is no longer available.' : 'Unconfirmed basket update');
        }
        added = true;

        // Apps add totals and savings panels outside the theme's refreshable
        // fragments. Match the native product form's cart-page navigation so
        // those panels, the discount and the subtotal are refreshed together.
        if (!inDrawer) {
          window.location.assign(cartUrl);
          return;
        }

        // Validate every fragment before touching the basket. Null sections can
        // accompany a successful add, so fall back to a fresh basket page.
        const fragments = sections.map(section => {
          const target = document.getElementById(section.id);
          const source = new DOMParser().parseFromString(data.sections?.[section.section] || '', 'text/html');
          const content = source.querySelector(section.selector);
          const destination = target?.querySelector(section.selector) || target;
          if (!content || !destination) throw new Error('Basket render unavailable');
          return { destination, content };
        });
        fragments.forEach(({ destination, content }) => { destination.innerHTML = content.innerHTML; });

        // Native cart listeners use this source for an already-rendered update.
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', productVariantId: form.elements.id.value, cartData: data });
        }
        const status = document.getElementById(inDrawer ? 'CartDrawer-LiveRegionText' : 'cart-live-region-text');
        if (status) status.textContent = `${form.dataset.addonName} added to your basket.`;
        checkoutControls.forEach(({ control, disabled }) => { control.disabled = disabled; });
        const focusTarget = inDrawer
          ? document.querySelector('#CartDrawer .wt-cart__cta')
          : document.querySelector('#main-cart-footer [name="checkout"]');
        focusTarget?.focus({ preventScroll: true });
      } catch (error) {
        if (added) {
          window.location.assign(cartUrl);
        } else {
          message.textContent = definitiveRejection
            ? error.message
            : 'We couldn’t confirm the update. Review your basket before adding this item again.';
          message.hidden = false;
          review.hidden = false;
          // An interrupted response may still have added the item. Require a
          // basket review, rather than risk adding it twice on a blind retry.
          if (!definitiveRejection) review.focus();
        }
      } finally {
        this.constructor.busy = false;
        buttons.forEach(control => { control.disabled = !added && !definitiveRejection; });
        checkoutControls.forEach(({ control, disabled }) => { control.disabled = disabled; });
        button.textContent = originalLabel;
        button.removeAttribute('aria-busy');
      }
    }
  });
}
